import React, { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from '../utils/toast';
import { useSubmitGuard } from '../utils/useSubmitGuard';
import { errorMessage } from '../utils/errorMessage';
import { useNavigate } from 'react-router-dom';
import { API_DETAILS } from '../App';

const ForgotPassword = () => {
    const [login, setLogin] = useState(0);
    

    const [otp, setOtp] = useState('');
    const [email, setEmail] = useState("");
    const [otpstep, setOtpstep] = useState(0); // Tracks the current step in the process
    const [enteredOtp, setEnteredOtp] = useState(''); // Stores the OTP entered by the user

    const navigate = useNavigate();
    const [, guard] = useSubmitGuard();

    // Navigating from inside the JSX meant navigate() ran during render, which
    // React may do twice and which fires before the success toast is painted.
    useEffect(() => {
        if (login === 1) navigate("/login");
    }, [login, navigate]);

    const { register, handleSubmit, watch, formState: { errors } } = useForm();

    // Submit email to request OTP
    const onSubmit = guard(async (data) => {
        // Under /auth, where the server has it (ForgotPasswordController) and where
        // it is public. Without /auth the request needed a sign-in the person
        // resetting a password does not have, and was refused with 401.
        const url = `${API_DETAILS.baseUrl}${API_DETAILS.PORT}${API_DETAILS.baseExtension}/auth/send-otp/${encodeURIComponent(data.email.trim())}`;
        const config = {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': '*',
                'Content-Type': 'application/json'
            }
        };
        try {
            await axios.get(url, config);
            // The code is no longer returned to the browser; the server holds its
            // hash and checks it when the reset is submitted.
            // The same answer whether or not the address has an account: saying which
            // told anyone who is a Youro patient. The code step says what to do if
            // nothing arrives, which covers a mistyped or unregistered address.
            toast.success("If there's a Youro account for that email, we've sent it a code. Enter it below to choose a new password.");
            setEmail(data.email.trim()); // Store the email used to request OTP
            setOtpstep(1); // Move to OTP entry step
        } catch (err) {
            // err.response is undefined when the request never reached the
            // server, so reading .data on it would throw inside the handler.
            const message =
                errorMessage(err, "We couldn't send a reset code to that email. Check the address and try again.");
            toast.error(message);
        }
    });

    // Moves to the new-password step. The code itself is checked by the server
    // when the reset is submitted — it is never sent to the browser, so there is
    // nothing here to compare against. A wrong code is rejected at that point.
    const codeVerify = () => {
        if (!enteredOtp || enteredOtp.trim().length === 0) {
            toast.error("Enter the code we emailed you.");
            return;
        }
        setOtpstep(2);
    };

    // Handle password reset
    const onChangePassword = guard(async (data) => {
        const newPasswordData = {
            username: email,
            password: data.password,
            // Proves the caller can read that mailbox. Without it the server
            // refuses the reset — previously it accepted any email and password.
            otp: enteredOtp
        };

        const url = `${API_DETAILS.baseUrl}${API_DETAILS.PORT}${API_DETAILS.baseExtension}/auth/password-reset`;
        const config = {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': '*',
                'Content-Type': 'application/json'
            }
        };
        try {
            await axios.put(url, newPasswordData, config);
            toast.success("Your password has been changed. Sign in with your new password.");
            setLogin(1); // Flag indicating successful login
            setOtpstep(3); // Completed reset step
        } catch (err) {
            // The server now rejects a wrong or expired code here, so the reason
            // has to reach the patient — "something went wrong" would leave them
            // retyping a correct password against an expired code.
            const message =
                errorMessage(err, "We couldn't reset your password. Request a new code and try again.");
            toast.error(message);
            // Send them back to the code step so they can request a fresh one.
            if (/code/i.test(message)) setOtpstep(1);
        }
    });

    return (
        <div>

            {/* Enter email */}
            {otpstep === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', margin: '5% 30%', justifyContent: 'center' }}>
                    <h2 style={{ marginBottom: '50px' }}>Forgot your password?</h2>
                    <label>Enter your email</label><br />
                    <input
                        className="input-field input-border"
                        style={{ width: '90%' }}
                        type="text"
                        {...register("email", {
                            required: true,
                            maxLength: 254,
                            pattern: /([-!#-'*+/-9=?A-Z^-~]+(\.[-!#-'*+/-9=?A-Z^-~]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?(\.[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?)+/
                        })}
                    />
                    {errors?.email?.type === "required" && <p className="error-text">This field is required</p>}
                    {errors?.email?.type === "maxLength" && <p className="error-text">Email cannot exceed 254 characters</p>}
                    {errors?.email?.type === "pattern" && <p className="error-text">Please enter a valid email</p>}
                    
                    <button type="button" className="btn-filled" style={{ marginTop: '5%', width: '87%' }} onClick={handleSubmit(onSubmit)}>Send me a code</button>
                </div>
            )}

            {otpstep === 1 && (
                <div id="digitsContainer" style={{ textAlign: 'center' }}>
                    <div>
                        <h5>Enter the 6-digit code we sent to <i><u>{email}</u></i></h5>
                    </div>
                    <input
                        type="text"
                        id="otpInput"
                        maxLength="6"
                        value={enteredOtp}
                        onChange={(e) => setEnteredOtp(e.target.value)}
                        aria-label="6-digit code"
                        placeholder="6-digit code"
                        style={{
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            width: '30%',
                            margin: '20px auto',
                            borderRadius: '8px',
                            border: '1px solid black',
                            padding: '10px',
                            boxShadow: 'none'
                        }}
                    />
                    <button
                        type="button"
                        id="verificationButton"
                        onClick={codeVerify}
                        className="verify-code-button"
                    >
                        Verify code
                    </button>
                    <div className="forgot-no-code">
                        <p style={{ margin: 0 }}>No code after a few minutes? Check your spam folder and the address you entered, or create an account if you don't have one yet.</p>
                        <div className="forgot-no-code-actions">
                            <button type="button" className="login-signup-link" onClick={() => { setEnteredOtp(''); setOtpstep(0); }}>Try a different address</button>
                            <button type="button" className="login-signup-link" onClick={() => navigate('/signup')}>Create an account</button>
                        </div>
                    </div>
                </div>
            )}



            {/*  Reset Password */}
            {otpstep === 2 && (
                <div style={{display: 'flex', flexDirection: 'column', margin: '5% 30%', justifyContent: 'center'}}>
                    <h2 style={{marginBottom: '40px'}}>Update Password</h2>
                    <div style={{margin: '2%'}}>
                        <label>New Password *</label>
                        <input
                            className="input-field input-border"
                            type="password"
                            {...register("password", {
                                required: true,
                                maxLength: 32,
                                minLength: 8
                            })}
                        />
                        {errors?.password?.type === "required" && <p className="error-text">This field is required</p>}
                        {errors?.password?.type === "maxLength" && <p className="error-text">Password cannot exceed 32 characters</p>}
                        {errors?.password?.type === "minLength" && <p className="error-text">Password length must be more than 8 characters</p>}
                    </div>
                    <div style={{ margin: '2%' }}>
                        <label>Confirm New Password *</label>
                        <input
                            className="input-field input-border"
                            type="password"
                            {...register("confirmPassword", {
                                validate: val => watch('password') === val
                            })}
                        />
                        {errors?.confirmPassword && <p className="error-text">Passwords must match</p>}
                    </div>
                    <button type="button" className="btn-filled" style={{ marginTop: '5%', width: '75%' }} onClick={handleSubmit(onChangePassword)}>Change Password</button>
                </div>
            )}

        
        </div>
    );
};

export default ForgotPassword;